import produce from 'immer';
import { omit } from 'lodash';
import { useQuery } from 'react-query';

import { getWidgetData } from 'api/endPoints/getWidgetData';

import { ICardLastComment } from 'components/common/Tables/Cells/DetailsCell/DetailsCell';

import {
    IAssignee,
    ICardIndicators,
    ICategory,
    IStatus,
    ITagList,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';
import { CardType } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';

export const listCardsDataQueryKey = 'listCardsData';

export const useQueryListCardsData = (params, endpoint?: string) => {
    const fixedParams = produce(params, (draft) => {
        draft.cardCachedFilter.filter.filters.forEach((item) => {
            omit(item, ['currentValue', 'isSelected']);
            return item;
        });
    });

    return useQuery<{ cardList: ISingleSmallCardResponse[]; numOfAllCards: number; numOfPages: number }, QueryError>(
        [listCardsDataQueryKey, params],
        () => getWidgetData(fixedParams, endpoint),
        {
            enabled: Boolean(params) && params?.cardCachedFilter.filter.filters?.length > 0,
        },
    );
};

export interface ISingleSmallCardResponse {
    id: number;
    isVirtual: boolean;
    type: {
        id: number;
        name: CardType;
        label: string;
    };
    taskActivityType: string;
    category: ICategory;
    title: string;
    parentActivity: string;
    startDate: number;
    endDate: number;
    dueDate: number | null;
    duration: number;
    assignee: IAssignee | null;
    rank: number;
    criticalityScore: number;
    float: number | null;
    tagList: ITagList[];
    versionDate: number;
    isTracked: boolean;
    details: string;
    isStartOverdue: boolean;
    isFinishOverdue: boolean;
    cardIndicators: ICardIndicators;
    status: IStatus;
    finishVariance: number;
    startVariance: number;
    taskVersionHashCode: string;
    originalStatus: IStatus | null;
    precedingActivities: number | null;
    succeedingActivites: number | null;
    precedingRisk: string | null;
    succeedingRisk: string | null;
    cardLastComment: ICardLastComment | null;
    submittedDurationComplete: number;
    plannedStartDate: number | null;
    plannedFinishDate: number | null;
    actualStartDate: number | null;
    actualFinishDate: number | null;
    completedDurationOverrun: number | null;
    grandParentActivity: string | null;
    baselineFinishDate: number | null;
    baselineStartDate: number | null;
    taskId?: number;
    metaDataId: number;
    taskHashCode: string;
    numOfPredecessors: number | null;
    numOfSuccessors: number | null;
    projectWeekStart: number | null;
    projectWeekFinish: number | null;
    projectMonthStart: number | null;
    projectMonthFinish: number | null;
    taskActivityId: string | null;
    plannedDurationInDays: number | null;
    actualDurationInDays: number | null;
    remainingDurationInDays: number | null;
    completedDurationInDays: number | null;
    currentDurationOverrun: number | null;
    isInterimChanged: boolean;
    severity: string | null;
    overrunPercent: number | null;
    orderInPage: number;
    uniqId: number;
    allBudgetResourceUnit: number | null;
    allActualResourceUnit: number | null;
    allResourceNameList: string[] | null;
    budgetResourceUnit: number | null;
    actualResourceUnit: number | null;
    remainingResourceUnit: null;
    resourceName: string | null;
    resourceType: string | null;
    isGenAiDetails: boolean;
}
